/**
 * A country's pyramid (docs/game-design.md §7.2, L1): Division I has one series, each division
 * below four times as many down to VI (1,024), then alternately the same and double. A series is
 * 8 fighters, named by division and number ("IV.12").
 */
import { LEAGUES, type DivisionName } from '../config/leagues';

/** Series in division `d` (1 = Division I). */
export function seriesInDivision(d: number): number {
  if (d < 1) return 0;
  if (d <= 6) return 4 ** (d - 1);
  return 1024 * 2 ** Math.floor((d - 6) / 2);
}

export function divisionName(d: number): DivisionName {
  const names = LEAGUES.divisionNames;
  return names[Math.min(d, names.length) - 1] ?? 'rookie';
}

const ROMAN: readonly [number, string][] = [
  [1000, 'M'],
  [900, 'CM'],
  [500, 'D'],
  [400, 'CD'],
  [100, 'C'],
  [90, 'XC'],
  [50, 'L'],
  [40, 'XL'],
  [10, 'X'],
  [9, 'IX'],
  [5, 'V'],
  [4, 'IV'],
  [1, 'I'],
];

export function roman(n: number): string {
  let out = '';
  let left = Math.max(0, Math.floor(n));
  for (const [v, s] of ROMAN) {
    while (left >= v) {
      out += s;
      left -= v;
    }
  }
  return out;
}

/** "IV.12"; just "I" for a division with a single series. */
export function seriesLabel(division: number, number: number): string {
  return seriesInDivision(division) > 1 ? `${roman(division)}.${number}` : roman(division);
}

/** Every fighter place in divisions 1..`divisions`. */
export function placesInPyramid(divisions: number): number {
  let n = 0;
  for (let d = 1; d <= divisions; d++) n += seriesInDivision(d) * LEAGUES.seriesSize;
  return n;
}
